/**
 * TimeEntryList — a fee earner's own time entries (or, embedded on a
 * matter page, every entry against that one matter).
 *
 * Edit and delete replace LawBillity's submit/approve workflow entirely
 * (Vanessa, 26 Sep 2026: "if we can edit and delete entries, we don't need
 * submit etc, this was cumbersome on lawbillity") — both actions only ever
 * show on a draft row, since hub.time_entries only allows editing/voiding
 * a draft (a billed/void/written-off entry is a record, not a form).
 *
 * Delete is one click: POST /time-entries/{id}/void requires a non-empty
 * reason, but prompting for one on every delete is exactly the friction
 * this ticket removes, so a fixed reason is sent automatically.
 */
import { Fragment, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../config/api";
import { errorMessage } from "../utils/errorMessage";
import TimeEntryForm from "./TimeEntryForm";
import { STAFF } from "../config/staffConfig";
import { ArrowDown, ArrowUp, Pencil, Trash2 } from "lucide-react";

const VOID_REASON = "Removed by fee earner";

const feeEarnerName = (email) => STAFF.find((s) => s.email === (email || "").toLowerCase())?.full_name || email || "";

function fmtValue(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "£0.00";
  return `£${n.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// BM-H3609 (5 Oct 2026), Vanessa: "what does draft mean here? it should be
// billed/unbilled." "draft" is the internal status for an entry that is
// still editable and not on an invoice -- meaningless to a fee earner, so
// the list says what it means for billing instead. Edit/delete still
// follow the internal status (see the row actions below); only the label
// changed.
function billingStatus(entry) {
  switch (entry.status) {
    case "billed": return "billed";
    case "void": return "void";
    case "written_off": return "written_off";
    default: return entry.chargeable === false ? "non_billable" : "unbilled";
  }
}

const STATUS_LABEL = {
  unbilled: "Unbilled",
  non_billable: "Non-billable",
  billed: "Billed",
  written_off: "Written off",
  void: "Void",
};

const STATUS_FILTER_OPTIONS = [
  { value: "", label: "All" },
  { value: "unbilled", label: "Unbilled" },
  { value: "non_billable", label: "Non-billable" },
  { value: "billed", label: "Billed" },
];

// from/to: inclusive entry_date range, used by the full list (BM-H3628).
const NO_FILTERS = { matter: "", narrative: "", status: "", from: "", to: "" };

function matchesFilters(entry, f) {
  // BM-H3628: the box also matches the client name when the entry has one
  // (only the full list asks the API for it).
  if (f.matter) {
    const q = f.matter.trim().toLowerCase();
    const hay = `${entry.matter_ref || ""} ${entry.client_name || ""}`.toLowerCase();
    if (!hay.includes(q)) return false;
  }
  if (f.from && (entry.entry_date || "") < f.from) return false;
  if (f.to && (entry.entry_date || "") > f.to) return false;
  if (f.narrative && !(entry.narrative || "").toLowerCase().includes(f.narrative.trim().toLowerCase())) return false;
  if (f.status && billingStatus(entry) !== f.status) return false;
  return true;
}

// min-w-0 lets the inputs shrink with their column instead of forcing the
// table wider than the panel (BM-H3618).
const FILTER_INPUT = "w-full min-w-0 bg-transparent border border-navy-border rounded px-1.5 py-1 text-xs text-slate-200 placeholder:text-slate-600";

// 30 Sep 2026: optional costs-schedule tag (dispute matters) -- the
// standard Precedent H / N260 categories. Short labels since this renders
// as a compact badge inline with the (already truncating) narrative cell.
const COST_TAG_LABEL = {
  attendance_on_client: "Client",
  attendance_on_other_side: "Other side",
  attendance_on_court: "Court",
  work_on_documents: "Documents",
  disclosure: "Disclosure",
  advocacy: "Advocacy",
};

// full (BM-H3628): the "All My Time Entries" page. Adds a Client column, a
// date range, the search over client or matter, and totals of what is shown.
// mine (BM-H3749): ask for the signed-in person's own entries explicitly. A
// director/admin who names nobody gets every fee earner's entries from the
// API. showFeeEarner adds a Fee earner column, for a director viewing
// colleagues' time.
export default function TimeEntryList({
  matterRef = "", feeEarnerEmail = "", showMatterColumn = true, full = false,
  mine = false, showFeeEarner = false, compactEmpty = false,
}) {
  const [entries, setEntries] = useState([]);
  const [clientNamesUnavailable, setClientNamesUnavailable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [voidingId, setVoidingId] = useState(null);
  const [filters, setFilters] = useState(NO_FILTERS);
  const setFilter = (key) => (e) => setFilters((f) => ({ ...f, [key]: e.target.value }));
  const filtersActive = Object.values(filters).some(Boolean);
  // BM-H3618: the Date header toggles newest/oldest (Vanessa: "if we can
  // toggle newest/oldest in one date column that's fine"). Not a filter, so
  // Clear filters leaves it alone. Array.sort is stable, so same-day
  // entries keep the order the API returned them in.
  const [newestFirst, setNewestFirst] = useState(true);
  const visibleEntries = entries
    .filter((entry) => matchesFilters(entry, filters))
    .sort((a, b) => (
      newestFirst
        ? (a.entry_date < b.entry_date ? 1 : a.entry_date > b.entry_date ? -1 : 0)
        : (a.entry_date < b.entry_date ? -1 : a.entry_date > b.entry_date ? 1 : 0)
    ));

  const totalHours = Math.round(visibleEntries.reduce((n, e) => n + (Number(e.duration_hours) || 0), 0) * 10) / 10;
  const totalValue = visibleEntries.reduce((n, e) => n + (Number(e.value) || 0), 0);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (matterRef) params.matter_ref = matterRef;
      if (feeEarnerEmail) params.fee_earner_email = feeEarnerEmail;
      if (mine) params.mine = true;
      if (full) params.with_client = true;
      const resp = await axios.get(`${API}/time-entries`, { params, withCredentials: true });
      setEntries(resp.data?.time_entries || []);
      setClientNamesUnavailable(!!resp.data?.client_names_unavailable);
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't load time entries."));
    }
    setLoading(false);
  }, [matterRef, feeEarnerEmail, full, mine]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const handleSaved = () => {
    setEditingId(null);
    fetchEntries();
  };

  const handleVoid = async (entry) => {
    setVoidingId(entry.id);
    try {
      await axios.post(
        `${API}/time-entries/${entry.id}/void`,
        { reason: VOID_REASON },
        { withCredentials: true },
      );
      toast.success("Time entry removed.");
      fetchEntries();
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't remove that time entry."));
    }
    setVoidingId(null);
  };

  if (loading) {
    return <div className="text-sm text-slate-500 py-4" data-testid="time-entry-list-loading">Loading...</div>;
  }

  if (entries.length === 0) {
    return <div className={compactEmpty ? "text-xs text-slate-500 py-1" : "text-sm text-slate-500 py-4"} data-testid="time-entry-list-empty">No time entries yet.</div>;
  }

  const colCount = (showMatterColumn ? 7 : 6) + (full ? 1 : 0) + (showFeeEarner ? 1 : 0);

  return (
    <div data-testid="time-entry-list">
      {full && (
        <div className="flex flex-wrap items-end gap-3 mb-3 text-xs" data-testid="time-entry-date-range">
          <label className="text-slate-500">
            From
            <input
              type="date" value={filters.from} onChange={setFilter("from")}
              className={`${FILTER_INPUT} mt-1 block w-auto`} aria-label="From date"
              data-testid="time-entry-filter-from"
            />
          </label>
          <label className="text-slate-500">
            To
            <input
              type="date" value={filters.to} onChange={setFilter("to")}
              className={`${FILTER_INPUT} mt-1 block w-auto`} aria-label="To date"
              data-testid="time-entry-filter-to"
            />
          </label>
          <p className="ml-auto text-slate-300" data-testid="time-entry-totals">
            {visibleEntries.length} {visibleEntries.length === 1 ? "entry" : "entries"}
            {" · "}{totalHours}h{" · "}{fmtValue(totalValue)}
          </p>
        </div>
      )}
      {full && clientNamesUnavailable && (
        <p className="mb-3 text-xs text-amber-400" data-testid="time-entry-client-names-unavailable">
          Client names could not be loaded just now, so client names are blank. Search by matter reference instead.
        </p>
      )}
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-navy-border text-slate-500">
            <th className="px-2 py-2 text-left">
              <button
                type="button"
                onClick={() => setNewestFirst((v) => !v)}
                className="inline-flex items-center gap-1 hover:text-slate-300"
                aria-label={newestFirst ? "Date, newest first (click for oldest first)" : "Date, oldest first (click for newest first)"}
                data-testid="time-entry-sort-date"
              >
                Date
                {newestFirst ? <ArrowDown className="w-3 h-3" /> : <ArrowUp className="w-3 h-3" />}
              </button>
            </th>
            {showFeeEarner && <th className="px-2 py-2 text-left">Fee earner</th>}
            {full && <th className="px-2 py-2 text-left">Client</th>}
            {showMatterColumn && <th className="px-2 py-2 text-left">Matter</th>}
            <th className="px-2 py-2 text-right">Hours</th>
            <th className="px-2 py-2 text-right">Value</th>
            <th className="px-2 py-2 text-left">Narrative</th>
            <th className="px-2 py-2 text-left">Status</th>
            <th className="px-2 py-2 w-20"></th>
          </tr>
          <tr className="border-b border-navy-border" data-testid="time-entry-filter-row">
            <th className="px-2 py-1.5"></th>
            {showFeeEarner && <th className="px-2 py-1.5"></th>}
            {full && (
              <th className="px-2 py-1.5 font-normal">
                <input
                  type="search" value={filters.matter} onChange={setFilter("matter")}
                  placeholder="Search client or matter" className={FILTER_INPUT} aria-label="Search by client or matter"
                  data-testid="time-entry-filter-matter"
                />
              </th>
            )}
            {showMatterColumn && (
              <th className="px-2 py-1.5 font-normal">
                {!full && (
                  <input
                    type="search" value={filters.matter} onChange={setFilter("matter")}
                    placeholder="Search matter" className={FILTER_INPUT} aria-label="Search by matter"
                    data-testid="time-entry-filter-matter"
                  />
                )}
              </th>
            )}
            <th className="px-2 py-1.5"></th>
            <th className="px-2 py-1.5"></th>
            <th className="px-2 py-1.5 font-normal">
              <input
                type="search" value={filters.narrative} onChange={setFilter("narrative")}
                placeholder="Filter narrative" className={FILTER_INPUT} aria-label="Filter by narrative"
                data-testid="time-entry-filter-narrative"
              />
            </th>
            <th className="px-2 py-1.5 font-normal">
              <select
                value={filters.status} onChange={setFilter("status")}
                className={FILTER_INPUT} aria-label="Filter by status"
                data-testid="time-entry-filter-status"
              >
                {STATUS_FILTER_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value} className="bg-navy-card">{o.label}</option>
                ))}
              </select>
            </th>
            <th className="px-2 py-1.5">
              {filtersActive && (
                <button
                  type="button" onClick={() => setFilters(NO_FILTERS)}
                  className="text-[11px] text-teal hover:underline whitespace-nowrap"
                  data-testid="time-entry-filters-clear"
                >
                  Clear
                </button>
              )}
            </th>
          </tr>
        </thead>
        <tbody>
          {visibleEntries.length === 0 && (
            <tr>
              <td colSpan={colCount} className="px-2 py-4 text-sm text-slate-500" data-testid="time-entry-list-no-match">
                No entries match these filters.{" "}
                <button type="button" onClick={() => setFilters(NO_FILTERS)} className="text-teal hover:underline">
                  Clear filters
                </button>
              </td>
            </tr>
          )}
          {visibleEntries.map((entry) => (
            <Fragment key={entry.id}>
              <tr className="border-b border-navy-border/40" data-testid="time-entry-row">
                <td className="px-2 py-2 whitespace-nowrap text-slate-300">{entry.entry_date}</td>
                {showFeeEarner && <td className="px-2 py-2 text-slate-300 whitespace-nowrap">{feeEarnerName(entry.fee_earner_email)}</td>}
                {full && <td className="px-2 py-2 text-slate-300">{entry.client_name || ""}</td>}
                {showMatterColumn && <td className="px-2 py-2 font-mono text-white">{entry.matter_ref}</td>}
                <td className="px-2 py-2 text-right text-white">
                  {entry.duration_hours}h{!entry.chargeable && <span className="ml-1 text-[10px] text-amber-400">(unbillable)</span>}
                </td>
                <td className="px-2 py-2 text-right text-slate-300">{fmtValue(entry.value)}</td>
                <td className="px-2 py-2 text-slate-400 max-w-xs truncate" title={entry.narrative}>
                  {entry.cost_tag && COST_TAG_LABEL[entry.cost_tag] && (
                    <span
                      className="mr-1.5 inline-block text-[10px] px-1.5 py-0.5 rounded-full border border-teal/40 text-teal align-middle"
                      data-testid={`time-entry-cost-tag-badge-${entry.id}`}
                    >
                      {COST_TAG_LABEL[entry.cost_tag]}
                    </span>
                  )}
                  {entry.narrative}
                </td>
                <td className="px-2 py-2 text-slate-400">{STATUS_LABEL[billingStatus(entry)]}</td>
                <td className="px-2 py-2">
                  {entry.status === "draft" && (
                    <div className="flex gap-1">
                      <button
                        type="button"
                        onClick={() => setEditingId(editingId === entry.id ? null : entry.id)}
                        className="text-slate-400 hover:text-teal p-1"
                        data-testid={`time-entry-edit-${entry.id}`}
                        title="Edit"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleVoid(entry)}
                        disabled={voidingId === entry.id}
                        className="text-slate-400 hover:text-red-400 p-1 disabled:opacity-50"
                        data-testid={`time-entry-delete-${entry.id}`}
                        title="Delete"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
              {editingId === entry.id && (
                <tr>
                  <td colSpan={colCount} className="px-2 py-3 bg-white/[0.02]">
                    <TimeEntryForm
                      matterRef={entry.matter_ref}
                      editingEntry={entry}
                      onSaved={handleSaved}
                      onCancel={() => setEditingId(null)}
                    />
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
    </div>
  );
}
